// "Does this watchlist hold anything this league can actually trade?"
//
// Client Rev 2:
//
//   "You select your Crypto watchlist for an all stocks league and QUSHi
//    incorrectly allows you to mark Crypto as the active Trade list with zero
//    eligible assets. … Since Crypto contains zero eligible assets, the Trade
//    screen initially returns No items. A later refresh then repopulates the
//    asset box from the league's default Stocks source. But the active list
//    label is never corrected, so you end up with Crypto in the header while
//    looking at the default Stocks search list. That last state is definitely a
//    bug. The header and the assets underneath must always represent the same
//    source."
//
//   "In a league, evaluate eligibility before allowing Use in Trade. If a
//    watchlist has zero eligible assets, disable Use in Trade and show: No
//    assets in this list are eligible for this league. Do not mark that list
//    Active. Do not change the current Trade wheel."
//
// So the question has to be answered BEFORE a list is allowed to become active,
// which means knowing the list's contents, which means fetching them. Every
// surface that offers the choice asks here, so the Watchlist screen, the mobile
// list detail and the Trade dropdown can never disagree about which lists are
// offerable.
//
// utils/leagueAssets holds the eligibility RULE (and mirrors the backend's
// LeagueAssets.php). This file only answers "how many rows of list N pass it",
// and caches the answer.

import { getWatchlistItems } from "../api/user";
import { filterEligible, isRestrictedLeague } from "./leagueAssets";

// The client's own words, so the wording is identical on every surface.
export const NO_ELIGIBLE_ASSETS_MESSAGE =
  "No assets in this list are eligible for this league";

// One page, deliberately large. The only thing any caller needs from this is
// zero vs non-zero, and a personal watchlist that runs past this many rows does
// not exist in the product — the Watchlist screen itself pages three at a time.
const SCAN_LIMIT = 200;

// key -> Promise<number>. The promise, not the number, so N components asking
// for the same list at the same moment share one request.
const inflight = new Map();

const keyFor = (game, listId) => `${game?.id ?? "main"}:${listId}`;

/**
 * How many rows of this watchlist this league will trade.
 *
 * Outside a league, or in an unrestricted one, this never fetches: there is
 * nothing to disqualify, so the answer is "enough" and callers carry on exactly
 * as they did before.
 */
export const fetchEligibleCount = (game, listId) => {
  if (listId == null) return Promise.resolve(0);
  // Not a restricted league (main site, "mixed", or a league created before
  // asset_category/asset_symbols existed) — nothing is ever ineligible, so do
  // not spend a request finding that out.
  if (!isRestrictedLeague(game)) return Promise.resolve(Infinity);

  const key = keyFor(game, listId);
  if (inflight.has(key)) return inflight.get(key);

  // `symbolsOnly` — the answer needs the rows, never their prices. Without it
  // this read makes two Polygon calls per row (25.5s for twelve rows, measured),
  // and the Trade dropdown runs it once per offered list, which is what the
  // client saw as "the screen became stuck".
  const promise = getWatchlistItems(listId, 1, SCAN_LIMIT, { symbolsOnly: true })
    .then((res) => {
      // A response with no `items` array is a response we did not understand,
      // not a list with nothing in it. Treating it as empty would disable the
      // button over a shape we failed to read — the same wrong answer the catch
      // below exists to avoid, so it gets the same fail-open treatment.
      if (!Array.isArray(res?.data?.items)) {
        inflight.delete(key);
        return Infinity;
      }
      return filterEligible(res.data.items, game).length;
    })
    .catch(() => {
      // A list we could not read must NOT be treated as empty — that would
      // disable a button over a dropped request. Fail open; the backend refuses
      // an ineligible order regardless, so the worst case is the old behaviour.
      inflight.delete(key);
      return Infinity;
    });

  inflight.set(key, promise);
  return promise;
};

/**
 * Eligible counts for several lists at once, as { [listId]: count }.
 *
 * Used by the Trade dropdown, which has to know about every list it offers
 * before the user picks one.
 */
export const fetchEligibleCounts = async (game, listIds, onEach) => {
  const ids = Array.from(new Set((listIds || []).filter((id) => id != null)));
  const out = {};
  if (!ids.length) return out;

  // ONE AT A TIME, awaited, not Promise.all.
  //
  // Firing all of them together is what the Trade panel's own price refresh
  // deliberately avoids ("so requests never pile up as a burst of concurrent
  // pending calls"), and for good reason: eight parallel reads of a 200-row page
  // left every one of them unanswered here, so no list was ever marked
  // unavailable. Serial is also perfectly fast enough — the answers are only
  // needed while a menu is open.
  //
  // `onEach` reports each answer as it lands, so the rows grey out progressively
  // instead of all at the end.
  for (const id of ids) {
    // eslint-disable-next-line no-await-in-loop
    const count = await fetchEligibleCount(game, id);
    out[String(id)] = count;
    if (typeof onEach === "function") onEach(String(id), count);
  }
  return out;
};

// Adding or removing an asset changes the answer. Called by the Watchlist
// screens after they mutate a list, so the next read is not served a stale
// count from before the edit.
export const forgetEligibleCount = (game, listId) => {
  inflight.delete(keyFor(game, listId));
};

export const forgetAllEligibleCounts = () => {
  inflight.clear();
};

// A count of 0 is the only blocking answer. Infinity means "not restricted" and
// undefined means "not looked up yet" — neither may block, because a button
// disabled while a request is in flight reads as a broken button.
export const blocksUseInTrade = (count) => count === 0;
